import { createFileRoute, Link } from '@tanstack/react-router';
import { usePageTitle } from '../../lib/hooks';
import { PageHeader } from '../../components/layout/PageHeader';
import { Button } from '../../components/ui/Button';
import { RefreshCw, Sparkles, Plus, Users, UserCheck } from 'lucide-react';
import { useDashboard, defineDashboardConfig } from '@repo/shared-dashboard';
import { DashboardPeriodSelector } from '@repo/web-dashboard';
import { getStaffDashboardMetrics } from '../../features/dashboard/network';
import { useStaffDashboard } from '../../features/dashboard/hooks';
import { format } from 'date-fns';
import {
  MemberStatsWidget,
  CrmPipelineWidget,
  GroupStatsWidget,
  AgentStatsWidget,
  TodayCollectionWidget,
  NewMembersWidget,
  CollectionChartWidget,
  RecentMembersWidget,
  GroupReportsWidget,
  ActiveAgentsWidget,
  RecentActivityWidget,
  TaskActivityWidget,
} from '../../features/dashboard/widgets';

export const Route = createFileRoute('/_auth/')({
  component: DashboardPage,
});

const dashboardConfig = defineDashboardConfig({
  resource: 'staff-metrics',
  statCards: [
    { id: 'members', label: 'Members', valueAccessor: 'membersCount' },
    { id: 'groups', label: 'Groups', valueAccessor: 'groupsCount' },
    { id: 'agents', label: 'Agents', valueAccessor: 'agentsCount' },
  ],
});

function DashboardPage() {
  usePageTitle('Dashboard');
  const dashboard = useDashboard(
    dashboardConfig,
    (period) => getStaffDashboardMetrics(period) as unknown as Promise<Record<string, unknown>>,
    { period: 'month' },
  );
  const metricsQuery = useStaffDashboard(dashboard.period);

  const isZeroState = metricsQuery.isSuccess && (metricsQuery.data?.membersCount ?? 0) === 0 && (metricsQuery.data?.groupsCount ?? 0) === 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <PageHeader
          title="Dashboard"
          subtitle={`Last refreshed: ${dashboard.lastRefreshed ? format(dashboard.lastRefreshed, 'hh:mm a') : '—'}`}
        />
        <div className="flex items-center gap-3">
          <DashboardPeriodSelector period={dashboard.period} onChange={dashboard.setPeriod} />
          <Button
            variant="ghost"
            onClick={() => dashboard.refresh()}
            disabled={dashboard.isRefreshing}
            className="p-1.5 text-text-secondary"
            title="Refresh dashboard"
          >
            <RefreshCw className={`w-4 h-4 ${dashboard.isRefreshing ? 'animate-spin text-accent-500' : ''}`} />
          </Button>
        </div>
      </div>

      {isZeroState && (
        <div className="bg-gradient-to-r from-accent-50/80 via-surface-1 to-surface-1 rounded-sm border border-surface-3 p-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 shadow-sm">
          <div className="flex items-start gap-3.5">
            <div className="p-2.5 bg-accent-500 text-white rounded-sm shadow-sm mt-0.5">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-text-primary">Welcome to Kraphene Financial Operations</h2>
              <p className="text-xs text-text-secondary mt-0.5 max-w-xl leading-relaxed">
                Your dashboard is live. Get started by creating your first Chit Group, onboarding Members, or assigning Field Collection Agents.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button asChild size="sm" variant="primary">
              <Link to="/schemes">
                <Plus className="w-3.5 h-3.5" />
                New Group
              </Link>
            </Button>
            <Button asChild size="sm" variant="outline">
              <Link to="/customers">
                <Users className="w-3.5 h-3.5 text-text-secondary" />
                Add Member
              </Link>
            </Button>
            <Button asChild size="sm" variant="outline">
              <Link to="/agents">
                <UserCheck className="w-3.5 h-3.5 text-text-secondary" />
                Assign Agent
              </Link>
            </Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-stretch">
        <div className="lg:col-span-5 flex flex-col justify-between gap-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 flex-1">
            <MemberStatsWidget period={dashboard.period} />
            <GroupStatsWidget period={dashboard.period} />
          </div>
          <div className="flex-1">
            <AgentStatsWidget period={dashboard.period} />
          </div>
        </div>
        <div className="lg:col-span-7 flex flex-col">
          <CrmPipelineWidget />
        </div>
      </div>

      <TodayCollectionWidget period={dashboard.period} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
        <NewMembersWidget period={dashboard.period} />
        <CollectionChartWidget period={dashboard.period} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
        <RecentMembersWidget period={dashboard.period} />
        <GroupReportsWidget period={dashboard.period} />
        <ActiveAgentsWidget period={dashboard.period} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
        <RecentActivityWidget period={dashboard.period} />
        <TaskActivityWidget period={dashboard.period} />
      </div>
    </div>
  );
}

